import React, {
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import {
  Alert,
  Card,
  Descriptions,
  Empty,
  Radio,
  Spin,
  Tag,
  Typography,
} from 'antd';

import {
  SyntheticBondBatchPreview,
  SyntheticBondCandidate,
  SyntheticBondCandidatePricePreview,
  syntheticBondCandidateId,
} from 'state';
import IssuedBondPriceChart, {
  IssuedBondPricePoint,
} from './IssuedBondPriceChart';

type Props = {
  candidatePreviews: Record<string, SyntheticBondCandidatePricePreview>;
  preloadComplete: boolean;
  preview: SyntheticBondBatchPreview;
};

export type CandidatePricePreviewHandle = {
  selectCandidate: (candidate: SyntheticBondCandidate) => void;
};

const candidateTenor = (candidate: SyntheticBondCandidate) =>
  Number(candidate.initial_tenor_years || candidate.tenor_years || 0);

const candidateIssueDate = (candidate: SyntheticBondCandidate) =>
  String(candidate.legal_issue_date || candidate.issue_date || '—').slice(
    0,
    10,
  );

const candidateExpiryDate = (candidate: SyntheticBondCandidate) =>
  String(candidate.legal_maturity_date || candidate.maturity_date || '—').slice(
    0,
    10,
  );

export const v2PreviewCandidates = (
  preview?: SyntheticBondBatchPreview,
): SyntheticBondCandidate[] => {
  if (!preview) return [];
  const direct = preview.items || preview.candidates;
  if (direct?.length) return direct;
  return (preview.tenor_summaries || []).flatMap(summary => {
    const candidates = summary.items || summary.candidates;
    return Array.isArray(candidates)
      ? (candidates as SyntheticBondCandidate[])
      : [];
  });
};

const CandidatePricePreview = React.forwardRef<
  CandidatePricePreviewHandle,
  Props
>(({ candidatePreviews, preloadComplete, preview }, ref) => {
  const candidates = useMemo(
    () =>
      [...v2PreviewCandidates(preview)].sort((left, right) => {
        const tenorDifference = candidateTenor(left) - candidateTenor(right);
        return tenorDifference
          ? tenorDifference
          : candidateIssueDate(left).localeCompare(candidateIssueDate(right));
      }),
    [preview],
  );
  const tenors = useMemo(
    () => [...new Set(candidates.map(candidateTenor))].sort((a, b) => a - b),
    [candidates],
  );
  const [selectedId, setSelectedId] = useState<string>();
  const selected =
    candidates.find(
      candidate => syntheticBondCandidateId(candidate) === selectedId,
    ) || candidates[0];
  const selectedTenor = selected ? candidateTenor(selected) : undefined;
  const tenorCandidates = candidates.filter(
    candidate => candidateTenor(candidate) === selectedTenor,
  );
  const itemId = syntheticBondCandidateId(selected) || '';
  const entry = itemId ? candidatePreviews[itemId] : undefined;
  const points = (entry?.price_series ||
    entry?.points ||
    entry?.issued_bond?.price_series ||
    []) as IssuedBondPricePoint[];
  const identifiers = selected?.identifiers || {};

  useImperativeHandle(
    ref,
    () => ({
      selectCandidate: candidate => {
        const candidateId = syntheticBondCandidateId(candidate);
        if (candidateId) setSelectedId(candidateId);
      },
    }),
    [],
  );

  useEffect(() => {
    const fallbackId = syntheticBondCandidateId(selected);
    if (fallbackId && selectedId !== fallbackId) setSelectedId(fallbackId);
  }, [selected, selectedId]);

  const selectTenor = (tenor: number) => {
    const firstIssue = candidates.find(
      candidate => candidateTenor(candidate) === tenor,
    );
    const firstIssueId = syntheticBondCandidateId(firstIssue);
    if (firstIssueId) setSelectedId(firstIssueId);
  };

  return (
    <Card
      className="synthetic-bonds__candidate-chart"
      title="Candidate valuation preview"
      extra={
        <Tag color={preloadComplete ? 'success' : 'processing'}>
          {Object.keys(candidatePreviews).length}/{candidates.length} curves
          preloaded
        </Tag>
      }
    >
      <div className="synthetic-bonds-v2__candidate-selectors">
        <div className="synthetic-bonds-v2__selector-row">
          <Typography.Text strong>Tenor family</Typography.Text>
          <Radio.Group
            disabled={!preloadComplete}
            optionType="button"
            value={selectedTenor}
            onChange={event => selectTenor(Number(event.target.value))}
          >
            {tenors.map(tenor => (
              <Radio.Button key={tenor} value={tenor}>
                {tenor}Y
              </Radio.Button>
            ))}
          </Radio.Group>
        </div>

        <div className="synthetic-bonds-v2__selector-row">
          <Typography.Text strong>Bond issue</Typography.Text>
          <Radio.Group
            disabled={!preloadComplete}
            optionType="button"
            value={itemId}
            onChange={event => {
              const next = tenorCandidates.find(
                candidate =>
                  syntheticBondCandidateId(candidate) === event.target.value,
              );
              const nextId = syntheticBondCandidateId(next);
              if (nextId) setSelectedId(nextId);
            }}
          >
            {tenorCandidates.map(candidate => {
              const id = syntheticBondCandidateId(candidate) || '';
              return (
                <Radio.Button key={id} value={id}>
                  {candidateIssueDate(candidate)} {'->'}{' '}
                  {candidateExpiryDate(candidate)}
                </Radio.Button>
              );
            })}
          </Radio.Group>
        </div>
      </div>

      {!preloadComplete ? (
        <div className="synthetic-bonds__candidate-loading">
          <Spin />
          <Typography.Text type="secondary">
            Receiving every candidate curve from the preview worker…
          </Typography.Text>
        </div>
      ) : !selected ? (
        <Empty description="No generated candidates were returned." />
      ) : !entry ? (
        <Alert
          message="The selected candidate curve was not preloaded."
          showIcon
          type="error"
        />
      ) : (
        <>
          <Descriptions bordered column={{ xs: 1, sm: 2 }} size="small">
            <Descriptions.Item label="Instrument">
              {selected.name ||
                identifiers.name ||
                selected.ticker ||
                identifiers.ticker ||
                itemId}
            </Descriptions.Item>
            <Descriptions.Item label="Issue → expiry">
              {candidateIssueDate(selected)} → {candidateExpiryDate(selected)}
            </Descriptions.Item>
            <Descriptions.Item label="Ticker / ISIN">
              {selected.ticker || identifiers.ticker || '—'} /{' '}
              {selected.isin || identifiers.isin || '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Fixed coupon">
              {typeof selected.coupon === 'number'
                ? `${selected.coupon.toFixed(6)}%`
                : '—'}
            </Descriptions.Item>
          </Descriptions>

          {points.length ? (
            <IssuedBondPriceChart
              chartIdPrefix="synthetic-v2-candidate"
              groupId="synthetic-v2-candidate"
              points={points}
            />
          ) : (
            <Empty description="No calculated price points were returned." />
          )}
        </>
      )}
    </Card>
  );
});

CandidatePricePreview.displayName = 'CandidatePricePreview';

export default React.memo(CandidatePricePreview);
